/**
 * InstallPrompt — banner/botão "Instalar app" do App Técnico (S6.3).
 *
 * - Android/Chrome: usa o prompt nativo (beforeinstallprompt).
 * - iOS/Safari: não há prompt nativo — mostra instruções de
 *   "Adicionar à Tela de Início".
 * - Já instalado (standalone) ou wrapper nativo: não aparece.
 *
 * Discreto: um banner fino no rodapé que o técnico pode dispensar.
 */
import { useEffect, useState } from "react";
import {
  canInstall,
  detectPlatform,
  isNativeApp,
  isStandalone,
  onInstallAvailabilityChange,
  promptInstall,
} from "./pwa.js";

export default function InstallPrompt() {
  const [available, setAvailable] = useState(canInstall());
  const [dismissed, setDismissed] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  const platform = detectPlatform();

  useEffect(() => {
    return onInstallAvailabilityChange((v) => setAvailable(v));
  }, []);

  // Não mostra se já é app nativo, já está instalado ou foi dispensado.
  if (isNativeApp() || isStandalone() || dismissed) return null;

  const isIOS = platform === "ios";
  // Android só mostra quando o prompt nativo está disponível.
  // iOS sempre pode mostrar (instalação é manual via Safari).
  if (!isIOS && !available) return null;

  async function handleInstall() {
    if (isIOS) {
      setShowIosHelp(true);
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "accepted") setDismissed(true);
  }

  return (
    <div className="fixed bottom-0 inset-x-0 z-30 px-3 pb-3
                    pointer-events-none">
      <div className="max-w-md mx-auto pointer-events-auto">
        {showIosHelp ? (
          <IosInstructions onClose={() => setShowIosHelp(false)} />
        ) : (
          <div className="flex items-center gap-3 bg-noc-panel
                          border border-noc-border rounded-xl
                          shadow-lg px-3 py-2.5">
            <div className="text-xl">📲</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold leading-tight">
                Instalar o App Técnico
              </div>
              <div className="text-[11px] text-noc-muted">
                Acesso rápido, tela cheia e uso offline básico.
              </div>
            </div>
            <button
              onClick={handleInstall}
              className="shrink-0 h-9 px-3 rounded-lg text-xs
                         font-semibold bg-noc-accent/15 text-noc-accent
                         border border-noc-accent/40"
            >
              Instalar
            </button>
            <button
              onClick={() => setDismissed(true)}
              aria-label="Dispensar"
              className="shrink-0 h-9 w-9 rounded-lg text-noc-muted
                         border border-noc-border"
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Passo a passo de instalação no iOS (Safari → Tela de Início). */
function IosInstructions({ onClose }) {
  return (
    <div className="bg-noc-panel border border-noc-border rounded-xl
                    shadow-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-lg">📲</span>
        <span className="text-sm font-semibold flex-1">
          Instalar no iPhone / iPad
        </span>
        <button
          onClick={onClose}
          aria-label="Fechar"
          className="h-8 w-8 rounded-lg text-noc-muted border
                     border-noc-border"
        >
          ✕
        </button>
      </div>
      <ol className="text-[12px] text-noc-text space-y-1.5 pl-1">
        <li>
          1. Abra este endereço no <b>Safari</b> (não funciona em
          outros navegadores no iOS).
        </li>
        <li>
          2. Toque no botão <b>Compartilhar</b> (o quadrado com a
          seta para cima).
        </li>
        <li>
          3. Escolha <b>Adicionar à Tela de Início</b>.
        </li>
        <li>
          4. Confirme em <b>Adicionar</b> — o app aparece como ícone.
        </li>
      </ol>
    </div>
  );
}
